import * as t from 'io-ts';

import {makeApiSerializableType, makeApiType} from './apiTypes';
import {
  HighlightTypesEnum,
  parseHighlight,
  RHighlightColorBase,
  RHighlightEmojiBase,
  RHighlightNoneBase,
} from './highlightBaseModel';

/*
 * Runtime type.
 */

const RBaseLens = t.readonly(
  t.type({
    id: t.number,
    namespace: t.string,
    name: t.string,
    createdAt: t.number,
    updatedAt: t.number,
    deletedAt: t.union([t.number, t.undefined]),
    inboxIds: t.readonlyArray(t.number),
    tagIds: t.readonlyArray(t.number),
    assigneeIds: t.readonlyArray(t.number),
  }),
  'lens',
);

const RBaseLensHighlightNone = t.intersection([RBaseLens, RHighlightNoneBase]);
const RBaseLensHighlightEmoji = t.intersection([RBaseLens, RHighlightEmojiBase]);
const RBaseLensHighlightColor = t.intersection([RBaseLens, RHighlightColorBase]);

const RLensHighlightNone = t.readonly(RBaseLensHighlightNone);
const RLensHighlightEmoji = t.readonly(RBaseLensHighlightEmoji);
const RLensHighlightColor = t.readonly(RBaseLensHighlightColor);

export const RLens = t.readonly(
  t.taggedUnion('type', [RBaseLensHighlightNone, RBaseLensHighlightEmoji, RBaseLensHighlightColor]),
  'lens',
);

export const RLenses = t.readonlyArray(RLens);

/*
 * Type.
 */

export interface LensHighlightNoneModel extends t.TypeOf<typeof RLensHighlightNone> {}
export interface LensHighlightEmojiModel extends t.TypeOf<typeof RLensHighlightEmoji> {}
export interface LensHighlightColorModel extends t.TypeOf<typeof RLensHighlightColor> {}
export type LensModel = LensHighlightNoneModel | LensHighlightEmojiModel | LensHighlightColorModel;

/*
 * IO methods.
 */

export function parseLens(src: any): LensModel {
  return {
    id: src.id,
    namespace: src.namespace,
    name: src.name,
    createdAt: src.created_at,
    updatedAt: src.updated_at,
    deletedAt: src.deleted_at,
    inboxIds: src.inbox_ids,
    tagIds: src.tag_ids,
    assigneeIds: src.assignee_ids,
    ...parseHighlight(src.highlight),
  };
}

export function parseLenses(src: any) {
  return src.lenses && src.lenses.map(parseLens);
}

export function serializeLens(lens: LensModel): any {
  return {
    name: lens.name,
    highlight: serializeHighlight(lens),
    namespace: lens.namespace,
    inbox_ids: lens.inboxIds,
    tag_ids: lens.tagIds,
    assignee_ids: lens.assigneeIds,
  };
}

function serializeHighlight(lens: LensModel) {
  switch (lens.type) {
    case HighlightTypesEnum.EMOJI:
      return lens.highlightEmoji;

    case HighlightTypesEnum.COLOR:
      return lens.highlightColor.toString();

    default:
      return null;
  }
}

/*
 * API type.
 */

export const ApiLens = makeApiSerializableType('lens', RLens, parseLens, serializeLens);
export const ApiLenses = makeApiType('lenses', RLenses, parseLenses);
